/**
 * Integration Account Selection Modal Component
 *
 * Modal for users to select which account to integrate with ENTER Health API
 */

import React from 'react';
import { Button } from './button';
import { cn } from '../utils/cn';
import { Dialog } from './dialog';
import { IconButton } from './icon-button';
import type { EnterHealthMeData } from '@ctrl-enter/types';

type AccountAccess = NonNullable<EnterHealthMeData['accountAccess']>[number];

interface IntegrationAccountSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedAccountId: string) => void;
  accounts: AccountAccess[];
  isLoading?: boolean;
}

export const IntegrationAccountSelectionModal: React.FC<IntegrationAccountSelectionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  accounts,
  isLoading = false,
}) => {
  const [selectedAccountId, setSelectedAccountId] = React.useState<string>('');

  // Reset selection when modal opens
  React.useEffect(() => {
    if (isOpen && accounts && accounts.length > 0) {
      setSelectedAccountId(accounts[0].accountId);
    }
  }, [isOpen, accounts]);

  const handleConfirm = () => {
    if (selectedAccountId) {
      onConfirm(selectedAccountId);
    }
  };

  const selectedAccount = accounts?.find(acc => acc.accountId === selectedAccountId);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      disableClose={isLoading}
      className="p-6"
    >
      <div data-testid="integration-account-selection-modal">
        {/* Close button */}
        <div className="absolute top-4 right-4">
          <IconButton
            aria-label="Close account selection modal"
            variant="ghost"
            size="sm"
            iconSize="sm"
            iconName="X"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          />
        </div>

        {/* Header */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-foreground mb-2">Select Integration Account</h2>
          <p className="text-sm text-muted-foreground">
            Choose which ENTER Health account you want to integrate with CTRL ENTER.
          </p>
        </div>

        {/* Account Selection */}
        <div className="space-y-3 mb-6">
          {accounts.map(account => (
            <button
              key={account.accountId}
              onClick={() => setSelectedAccountId(account.accountId)}
              className={cn(
                'w-full p-4 rounded-md border-2 transition-all text-left',
                'hover:border-primary/50',
                selectedAccountId === account.accountId
                  ? 'border-primary bg-primary/5'
                  : 'border-border bg-background'
              )}
              disabled={isLoading}
            >
              <div className="flex items-center gap-3">
                {account.logoUrl && (
                  <img
                    src={account.logoUrl}
                    alt={`${account.name} logo`}
                    className="w-10 h-10 rounded object-contain"
                    onError={e => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                )}
                <div className="flex-1">
                  <div className="font-medium text-foreground">{account.name}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Role: {account.roleId.replace(/_/g, ' ')}
                  </div>
                </div>
                {selectedAccountId === account.accountId && (
                  <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>

        {/* Confirmation Message */}
        <div className="mb-6 p-4 rounded-md bg-muted/50 border border-border">
          <p className="text-sm text-foreground">
            <span className="font-medium">Confirm:</span> You are about to add the integration for{' '}
            <span className="font-semibold">{selectedAccount?.name}</span>. This will store your API
            credentials securely.
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3 justify-end">
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={!selectedAccountId || isLoading}
          >
            {isLoading ? 'Adding...' : 'Add Integration'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};
